import type { VideoAvatarMode } from '@mfc/mfc-video-sdk'
import { useSyncExternalStore } from 'react'
import { VideoEvent, VideoType } from '../../constants/enums'
import { GLOBAL_VIDEO_CACHE, VIDEOSTATE } from '../../state'
import { pubSubVideo } from '../../state/pubSub'
import { safeObj } from '../../utilities/safe'

export const getVideoPlayingStatus = (id: number) => () =>
    VIDEOSTATE.videos[id]?.cache?.playing
const getVideoInstance = (id: number) => () =>
    VIDEOSTATE.videos[id]?.instance ?? null
const getVideoAvatar = (id: number) => () =>
    VIDEOSTATE.videos[id]?.videoAvatar ?? null
const getDimensions = (videoType: VideoType) => () =>
    safeObj(GLOBAL_VIDEO_CACHE[videoType]?.dimensions)

const subPlaying = (id: number) => (callback: () => void) =>
    pubSubVideo.on(VideoEvent.PLAYING + '::' + id, callback)
const subInstance = (id: number) => (callback: () => void) =>
    pubSubVideo.on(VideoEvent.INSTANCE + '::' + id, callback)
const subDimensions = (videoType: VideoType) => (callback: () => void) =>
    pubSubVideo.on(VideoEvent.DIMENSIONS + '::' + videoType, callback)
const subVideoAvatar = (id: number) => (callback: () => void) =>
    pubSubVideo.on(VideoEvent.AVATAR + '::' + id, callback)

export const useVideoPlaying = (id: number): boolean | null => {
    const playing = useSyncExternalStore(
        subPlaying(id),
        getVideoPlayingStatus(id),
        getVideoPlayingStatus(id),
    )

    return playing
}

/**
 * The live player instance for a model, or null. Its identity changes when
 * the player is torn down and rebuilt in place (show-type change, camserver
 * move, an offline model's rebuild — see resolveInstance in state.video),
 * which state.video announces on its own channel as soon as
 * the new player has rendered: its control bar DOM exists, whether or not
 * the stream ever plays. Use this as an effect dep to re-apply imperative
 * control-bar work that dies with the old player DOM. The playing channel
 * cannot stand in for it: it publishes only a change of the flag, and a
 * rebuild from a paused or placarded player goes false -> false.
 */
export const useVideoInstance = (id: number) =>
    useSyncExternalStore(
        subInstance(id),
        getVideoInstance(id),
        getVideoInstance(id),
    )

/** The SDK video avatar presentation a model's player has on screen, else null */
export const useVideoAvatar = (id: number): VideoAvatarMode | null =>
    useSyncExternalStore(
        subVideoAvatar(id),
        getVideoAvatar(id),
        getVideoAvatar(id),
    )

export function useVideoDimensions(videoType: VideoType) {
    const dimensions = useSyncExternalStore(
        subDimensions(videoType),
        getDimensions(videoType),
        getDimensions(videoType),
    )

    return dimensions
}
